/**
 * Phase 16 Plan 16-03 — TypeScript wire types for the Work Order Management feature.
 *
 * Mirrors the Plan 16-01 backend DTO contract (camelCase per Phase 1/2 JsonNamingPolicy).
 * WorkOrderStatus / WorkOrderAuditAction are string-literal const objects matching
 * JsonStringEnumConverter PascalCase output.
 *
 * Backend uses DateOnly (yyyy-MM-dd) for date columns — TS uses `string` here;
 * form layers parse/format as needed.
 */

import type { PaginatedResult } from '@/types/roles';

/** Re-export for convenience */
export type { PaginatedResult };

/** Status values — mirrors WorkOrderStatus enum (Draft=1, Active=2, Completed=3, Cancelled=4). */
export const WorkOrderStatus = {
  Draft: 'Draft',
  Active: 'Active',
  Completed: 'Completed',
  Cancelled: 'Cancelled',
} as const;
export type WorkOrderStatus = (typeof WorkOrderStatus)[keyof typeof WorkOrderStatus];

/** Audit action values — mirrors WorkOrderAuditAction enum. */
export const WorkOrderAuditAction = {
  Created: 'Created',
  Edited: 'Edited',
  MachineAdded: 'MachineAdded',
  MachineRemoved: 'MachineRemoved',
  Activated: 'Activated',
  Completed: 'Completed',
  Reopened: 'Reopened',
  Cancelled: 'Cancelled',
  KpiAdded: 'KpiAdded',
} as const;
export type WorkOrderAuditAction = (typeof WorkOrderAuditAction)[keyof typeof WorkOrderAuditAction];

/** A linked site embedded in WorkOrderDetail — mirrors WorkOrderSiteDto. */
export interface WorkOrderSiteItem {
  readonly id: number;
  readonly siteId: number;
  readonly siteName: string;
  readonly linkedAt: string; // ISO datetime
  readonly unlinkedAt: string | null;
  readonly linkedByUserId: number;
}

/**
 * True when a site link is still active. The API only returns active links, but it has
 * historically omitted `unlinkedAt` from the payload — treat a missing value as active
 * rather than silently dropping the row.
 */
export function isActiveSiteLink(s: { readonly unlinkedAt?: string | null }): boolean {
  return s.unlinkedAt === null || s.unlinkedAt === undefined;
}

/** A row in `GET /api/workorders` (paginated list) — mirrors WorkOrderListItemDto. */
export interface WorkOrderListItem {
  readonly id: number;
  readonly woNumber: string;
  readonly clientId: number | undefined;
  readonly clientName: string | undefined;
  /** Active site names for multi-site WOs — empty when the WO has no sites linked. */
  readonly siteNames: readonly string[] | undefined;
  /** Count of active site links; always consistent with `siteNames.length`. */
  readonly sitesCount: number;
  readonly startDate: string; // yyyy-MM-dd
  readonly endDate: string; // yyyy-MM-dd
  readonly status: WorkOrderStatus;
  readonly machinesCount: number;
  readonly kpisCount: number;
}

/** An active machine link embedded in WorkOrderDetail — mirrors WorkOrderMachineDto. */
export interface WorkOrderMachineItem {
  readonly id: number;
  readonly machineId: number;
  readonly machineCode: string;
  readonly machineName: string;
  /** Denormalized `vehicle_number` custom field — shown as `Name (Vehicle Number)`; null when unrecorded. */
  readonly vehicleNumber: string | null;
  readonly machineTypeName: string;
  readonly linkedAt: string; // ISO datetime
  readonly unlinkedAt: string | null;
}

/** Full detail from `GET /api/workorders/:id` — mirrors WorkOrderDetailDto. */
export interface WorkOrderDetail {
  readonly id: number;
  readonly woNumber: string;
  /** @deprecated legacy single-site field — kept for backward compat with LinkMachinesDialog */
  readonly siteId: number | undefined;
  /** @deprecated legacy single-site field — kept for backward compat with existing overview tab */
  readonly siteName: string | undefined;
  readonly clientId: number | undefined;
  readonly clientName: string | undefined;
  /** Active linked sites (D-228 multi-site) */
  readonly sites: readonly WorkOrderSiteItem[] | undefined;
  /** Entry count — non-zero means clientId is immutable */
  readonly entryCount: number | undefined;
  readonly description: string | null;
  readonly startDate: string; // yyyy-MM-dd
  readonly endDate: string; // yyyy-MM-dd
  readonly status: WorkOrderStatus;
  readonly completionDate: string | null; // yyyy-MM-dd
  readonly activatedByUserId: number | null;
  readonly activatedAt: string | null; // ISO datetime
  readonly completedByUserId: number | null;
  readonly completedAt: string | null; // ISO datetime
  readonly cancelledByUserId: number | null;
  readonly cancelledAt: string | null; // ISO datetime
  readonly cancellationReason: string | null;
  readonly machines: readonly WorkOrderMachineItem[];
  readonly kpisCount: number;
}

/** A single audit row from `GET /api/workorders/:id/audit` — mirrors WorkOrderAuditLogDto. */
export interface WorkOrderAuditLogItem {
  readonly id: number;
  readonly action: WorkOrderAuditAction;
  readonly at: string; // ISO datetime
  readonly byUserId: number;
  readonly byUserName: string | null;
  readonly details: string | null;
}

/** Body for `POST /api/workorders` — mirrors CreateWorkOrderRequest (D-229). */
export interface CreateWorkOrderRequest {
  readonly clientId: number;
  readonly siteIds: readonly number[];
  readonly woNumber?: string;
  readonly description?: string;
  readonly startDate: string; // yyyy-MM-dd
  readonly endDate: string; // yyyy-MM-dd
  readonly machineIds: readonly number[];
}

/** Body for `PUT /api/workorders/:id` — mirrors UpdateWorkOrderRequest. */
export interface UpdateWorkOrderRequest {
  readonly clientId?: number;
  /**
   * Client feedback (2026-09) — the WO number is editable while the WO is Draft or Active.
   * Omit the field to leave it unchanged; the backend rejects a duplicate with 409
   * WO_NUMBER_DUPLICATE and a Completed/Cancelled WO with 409 WO_NUMBER_FROZEN.
   */
  readonly woNumber?: string;
  readonly description?: string;
  readonly startDate: string; // yyyy-MM-dd
  readonly endDate: string; // yyyy-MM-dd
}

/** Body for `POST /api/workorders/:id/sites/add` and `/sites/remove` — mirrors AddRemoveSitesRequest. */
export interface AddRemoveSitesRequest {
  readonly siteIds: readonly number[];
}

/** Body for `POST /api/workorders/:id/machines/add` and `/remove` — mirrors AddRemoveMachinesRequest. */
export interface AddRemoveMachinesRequest {
  readonly machineIds: readonly number[];
}

/** Body for `POST /api/workorders/:id/cancel` — mirrors CancelWorkOrderRequest. */
export interface CancelWorkOrderRequest {
  readonly reason: string;
}

/** Response from `POST /api/workorders/:id/complete` — mirrors CompleteWorkOrderResponse. */
export interface CompleteWorkOrderResponse {
  readonly workOrder: WorkOrderDetail;
  readonly warnings: readonly string[];
}

/** Query params for `GET /api/workorders` — mirrors WorkOrderListQuery. */
export interface WorkOrderListQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly q?: string;
  readonly clientId?: number;
  readonly siteId?: number;
  readonly status?: WorkOrderStatus;
  readonly startDateFrom?: string;
  readonly startDateTo?: string;
}

/** Query params for `GET /api/workorders/:id/audit` — mirrors WorkOrderAuditListQuery. */
export interface WorkOrderAuditListQuery {
  readonly page?: number;
  readonly pageSize?: number;
}
